import { useState } from "react";
import { Distributor } from "../../../models/Product";
import { del, get, post } from "../../../api/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Select,
  Snackbar,
  Switch,
  TextField
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import AddCircleIcon from "@material-ui/icons/AddCircle";
import SaveIcon from "@material-ui/icons/Save";
import { Alert, Skeleton } from "@material-ui/lab";
import { ProblemDetails } from "../../../models/Http";
import { useI18n } from "../../../hooks/useI18n";

export const ManageTenantDistributors = ({
  tenantId
}: {
  tenantId: string;
}) => {
  const [isNew, setNew] = useState(false);
  const [newDistributor, setNewDistributor] = useState("");
  const queryClient = useQueryClient();
  const { t } = useI18n();

  function fetchDistributorsByTenant(): Promise<ReadonlyArray<Distributor>> {
    return get(`/v1/tenants/${tenantId}/distributors`).then(response =>
      response.json()
    );
  }
  function fetchDistributors(): Promise<ReadonlyArray<Distributor>> {
    return get(`/v1/tenants/distributors`).then(response => response.json());
  }

  async function postDistributor() {
    const response = await post(
      `/v1/tenants/${tenantId}/distributors`,
      newDistributor
    );

    if (!response.ok) {
      const error = await response.json();
      throw error as ProblemDetails;
    }
    return response;
  }

  async function deleteDistributor(id: string) {
    const response = await del(`/v1/tenants/${tenantId}/distributors/${id}`);

    if (!response.ok) {
      const error = await response.json();
      throw error as ProblemDetails;
    }
    return response;
  }

  const tenantDistributors = useQuery({
    queryKey: ["distributors"],
    queryFn: fetchDistributorsByTenant
  });

  const distributors = useQuery({
    queryKey: ["alldistributors"],
    queryFn: fetchDistributors
  });

  const addMutation = useMutation<Response, ProblemDetails>({
    mutationFn: postDistributor,
    onSuccess: () => {
      setNewDistributor("");
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ["distributors"] });
    }
  });

  const deleteMutation = useMutation<Response, ProblemDetails, string>({
    mutationFn: deleteDistributor,
    onSuccess: () => {
      setNewDistributor("");
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ["distributors"] });
    }
  });

  return (
    <Box>
      <Snackbar
        open={
          addMutation.isSuccess ||
          addMutation.isError ||
          deleteMutation.isSuccess ||
          deleteMutation.isError
        }
        autoHideDuration={3000}
        onClose={() => {
          addMutation.reset();
          deleteMutation.reset();
        }}
      >
        <div>
          {addMutation.isError && (
            <Alert severity={"error"} title={addMutation.error.title}>
              {addMutation.error.detail}
            </Alert>
          )}
          {addMutation.isSuccess && (
            <Alert severity={"success"}>
              {t("common.messages.successfullySavedDistributor")}
            </Alert>
          )}
          {deleteMutation.isError && (
            <Alert severity={"error"} title={deleteMutation.error.title}>
              {deleteMutation.error.detail}
            </Alert>
          )}
          {deleteMutation.isSuccess && (
            <Alert severity={"success"}>
              {t("common.messages.successfullyDeletedDistributor")}
            </Alert>
          )}
        </div>
      </Snackbar>
      <List>
        {tenantDistributors.data?.map(distributor => (
          <ListItem>
            <ListItemText primary={distributor.name} />
            <ListItemSecondaryAction>
              <IconButton
                edge="end"
                aria-label="delete"
                color="secondary"
                onClick={() => deleteMutation.mutate(distributor.id)}
              >
                <DeleteIcon />
              </IconButton>
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>
      <Box display="flex" alignItems="center" gridGap="1rem">
        {isNew && (
          <TextField
            style={{ flexGrow: 1 }}
            label={t("common.formFields.newDistributor")}
            required
            value={newDistributor}
            onChange={e => setNewDistributor(e.target.value)}
          />
        )}

        {distributors.isLoading && !isNew && (
          <Skeleton style={{ flexGrow: 1 }} height={50} variant="text" />
        )}

        {distributors.data && !isNew && (
          <FormControl style={{ flexGrow: 1 }}>
            <InputLabel required>
              {t("common.formFields.distributor")}
            </InputLabel>

            <Select
              value={newDistributor}
              onChange={e => setNewDistributor(e.target.value as string)}
            >
              {[...new Set(distributors.data.map(d => d.name))].map(dis => (
                <MenuItem key={dis} value={dis}>
                  {dis}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* <CircularProgress /> */}
        <IconButton
          aria-label="add"
          size="medium"
          style={{ color: "green", marginTop: 16 }}
          onClick={() => addMutation.mutate()}
        >
          <AddCircleIcon />
        </IconButton>
        <FormControlLabel
          style={{ marginTop: 16 }}
          control={
            <Switch
              checked={isNew}
              onChange={e => setNew(e.target.checked)}
              name={t("common.formFields.newDistributor")}
              color="primary"
            />
          }
          label={t("common.actions.addNew")}
          labelPlacement="end"
        />
      </Box>
    </Box>
  );
};
